/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';
import { animateResizeTransform } from './animateResizeTransform';
import {
  maxPressure,
  maxTemperature,
  minPressure,
  minTemperature,
  minEmagramTemperature,
} from './constants';
import type { Margin } from './drawUtils';
import { createScales, initializeSvg, SOUNDING_COLORS } from './drawUtils';
import { useSharedMouseY } from './SharedMouseYProvider';
import type {
  SoundingDiagramType,
  SoundingFeature,
} from '../../api/edr-api/types';
import {
  constructTooltipData,
  drawSoundingsTooltip,
  extractPressureFromY,
} from '../SoundingsTooltip/utils';
import { drawIsobars } from '../StaticLines/drawIsobars';
import { drawWindBarbs } from '../WindBarbs/utils';

const shadow =
  '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 4px 0 rgba(0, 0, 0, 0.12), 0 2px 4px 0 rgba(0, 0, 0, 0.14)';

const soundingsStyle = {
  tooltip: {
    display: 'block',
    position: 'absolute',
    borderRadius: '1.5px',
    backgroundColor: 'geowebColors.soundings.tooltip.background',
    boxShadow: shadow,
    padding: '2px 4px',
    pointerEvents: 'none',
    zIndex: 500,
  },
  wind: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
};

const WIND_BARB_OFFSET_X = 35;

const getVerticalOnlyTransform = (scale: number, yOffset: number): string => {
  // Keep x-axis unchanged, apply shared zoom on y-axis only.
  return `matrix(1, 0, 0, ${scale}, 0, ${yOffset})`;
};

/**
 * Check if the sounding data is effectively empty.
 * Data can be empty in two ways:
 * 1. No sources selected: data = []
 * 2. No locations selected: data = [[], []] (sources exist but all have empty arrays)
 */
const isDataEmpty = (data: SoundingFeature[][]): boolean => {
  if (!data || data.length === 0) {
    return true;
  }
  // Check if all inner arrays are empty (no locations selected)
  return data.every((features) => features.length === 0);
};

export interface WindBarbsProps {
  d3GraphId: string;
  data: SoundingFeature[][];
  svgDimensions: {
    width: number;
    height: number;
    margin: Margin;
  };
  showTooltip?: boolean;
  diagramType?: SoundingDiagramType;
  animateResize?: boolean;
}

export const WindBarbs: React.FC<WindBarbsProps> = ({
  d3GraphId,
  data,
  svgDimensions,
  showTooltip = true,
  diagramType = 'SKEWT',
  animateResize = false,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const windRef = useRef<HTMLDivElement | null>(null);
  const prevInnerHeightRef = useRef<number | null>(null);

  // Refs for shared mouse effect
  const yScaleRef = useRef<d3.ScaleLinear<number, number> | null>(null);
  const marginRef = useRef<Margin | null>(null);
  const widthRef = useRef<number>(0);
  const svgRectRef = useRef<DOMRect | null>(null);
  const tooltipDataRef = useRef<ReturnType<typeof constructTooltipData> | null>(
    null,
  );
  // Must point to the inner translated <g> to match Soundings zoom coordinate space.
  const zoomTargetRef = useRef<SVGElement | null>(null);

  const { clientY, verticalZoom, resetVerticalZoom } = useSharedMouseY();

  // Set SVG dimensions (required props; no optional chaining needed)
  const { width: baseWidth, height: baseHeight, margin } = svgDimensions;
  const width = baseWidth - (margin.left + margin.right);
  const height = baseHeight - (margin.top + margin.bottom);

  useEffect(() => {
    if (!svgRef.current || width <= 0 || height <= 0) {
      return;
    }

    const root = d3.select(svgRef.current);
    root.selectAll('*').remove();

    const svg = initializeSvg({
      svgRef,
      width,
      height,
      margin,
      diagramType,
      d3GraphId,
    }) as unknown as d3.Selection<SVGElement, unknown, HTMLElement, unknown>;

    // When Soundings is dragged closed, reset the zoom level and animate the Wind Barbs to resize back to the height where dragging started
    if (animateResize) {
      resetVerticalZoom(d3GraphId);
      animateResizeTransform(svg, margin, height, prevInnerHeightRef);
    }

    const svgRectangle = svgRef.current.getBoundingClientRect();
    svgRectRef.current = svgRectangle;
    marginRef.current = margin;
    widthRef.current = width;

    const minT =
      diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;
    const maxT = maxTemperature;

    const [xScale, yScale] = createScales(
      diagramType,
      width,
      height,
      minPressure,
      maxPressure,
      minT,
      maxT,
    );
    yScaleRef.current = yScale;

    drawIsobars({
      diagramType,
      minTemperature: minT,
      maxTemperature: maxT,
      xScale,
      yScale,
      svg,
    });

    data.forEach((features, index) => {
      const color = SOUNDING_COLORS[index] || SOUNDING_COLORS[0];
      const windData = features.map((feature) => ({
        windDirection: feature.properties.windDirection,
        windSpeed: feature.properties.windSpeed,
        pressure: feature.properties.pressure,
      }));
      drawWindBarbs({
        svgRef,
        plotGroup: svg,
        yScale,
        data: windData,
        width,
        margin,
        barbColor: color,
        columnOffsetX: index * WIND_BARB_OFFSET_X,
        verticalScale: verticalZoom.scale,
      });
    });

    // Clear or update tooltip data based on whether we have actual data
    tooltipDataRef.current = isDataEmpty(data)
      ? null
      : constructTooltipData(data);

    // Update previous inner height after drawing so animation uses the prior value
    prevInnerHeightRef.current = height;

    const zoomTarget = svg.node();
    if (!zoomTarget) {
      return;
    }
    zoomTargetRef.current = zoomTarget;

    // Initialize with shared zoom state from Soundings.
    d3.select(zoomTarget).attr(
      'transform',
      getVerticalOnlyTransform(verticalZoom.scale, verticalZoom.y),
    );

    // eslint-disable-next-line consistent-return, @typescript-eslint/explicit-function-return-type
    return () => {
      zoomTargetRef.current = null;
      root.selectAll('*').remove();
    };
  }, [
    data,
    diagramType,
    width,
    height,
    d3GraphId,
    margin,
    animateResize,
    verticalZoom.scale,
    verticalZoom.y,
    resetVerticalZoom,
  ]);

  useEffect(() => {
    if (!zoomTargetRef.current) {
      return;
    }

    d3.select(zoomTargetRef.current).attr(
      'transform',
      getVerticalOnlyTransform(verticalZoom.scale, verticalZoom.y),
    );
  }, [verticalZoom]);

  useEffect(() => {
    if (!showTooltip || !svgRef.current || !windRef.current) {
      windRef.current && (windRef.current.style.display = 'none');
      return;
    }

    // Hide tooltip when data is empty (all sources/locations removed)
    if (isDataEmpty(data)) {
      windRef.current.style.display = 'none';
      return;
    }

    const rect = svgRectRef.current ?? svgRef.current.getBoundingClientRect();
    if (clientY == null || clientY < rect.top || clientY > rect.bottom) {
      windRef.current.style.display = 'none';
      return;
    }

    if (!yScaleRef.current || !marginRef.current || !tooltipDataRef.current) {
      return;
    }

    windRef.current.style.display = 'block';

    const synthetic = {
      clientX: rect.left + rect.width / 2,
      clientY,
      pageY: clientY + window.scrollY,
      target: svgRef.current,
      currentTarget: svgRef.current,
    } as unknown as MouseEvent;

    const pointerY = clientY - rect.top - marginRef.current.top;
    const unscaledY = (pointerY - verticalZoom.y) / verticalZoom.scale;
    const hoveredPressure = extractPressureFromY(
      diagramType,
      unscaledY,
      yScaleRef.current,
    );
    const transformedY =
      yScaleRef.current(hoveredPressure) * verticalZoom.scale + verticalZoom.y;
    const pressureLineY = transformedY + marginRef.current.top;

    const windRefd3 = d3.select<HTMLDivElement | null, unknown>(
      windRef.current,
    );

    drawSoundingsTooltip({
      event: synthetic,
      svgRectangle: rect,
      margin: marginRef.current,
      width: widthRef.current,
      hoveredPressure,
      windRefd3,
      tooltipData: tooltipDataRef.current,
      diagramType,
    });

    const tooltipHeight = windRef.current.getBoundingClientRect().height;
    const centeredTop = pressureLineY - tooltipHeight / 2;
    const clampedTop = Math.max(
      0,
      Math.min(centeredTop, rect.height - tooltipHeight),
    );
    windRef.current.style.top = `${clampedTop}px`;
  }, [
    clientY,
    showTooltip,
    diagramType,
    data,
    verticalZoom.scale,
    verticalZoom.y,
  ]);

  return (
    <Box ref={canvasRef} sx={{ position: 'relative' }}>
      <svg ref={svgRef} />
      <Box
        data-testid="wind-tooltip"
        ref={windRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.wind,
        }}
      />
    </Box>
  );
};
